// kHtmlParserCore — pure TypeScript, zero-dependency HTML tokenizer and DOM tree builder.
//
// Allows kHtmlToPdfMakeCore to execute cleanly in Node.js / Azure Functions / serverless runtimes
// without jsdom, @xmldom, or browser DOMParser globals.

import { getCoreUtils } from "./kCoreHandler";
import { kHtmlCloseIndexCore } from "./kHtmlCloseIndexCore";
import { kCssDeclarationCore } from "./kCssDeclarationCore";

export interface IkDomNode {
    nodeType: number; // 1 = Element, 3 = Text
    nodeValue: string | null;
    parentNode: IkDomElement | null;
}

export interface IkDomTextNode extends IkDomNode {
    nodeType: 3;
    nodeValue: string;
}

export interface IkDomElement extends IkDomNode {
    nodeType: 1;
    tagName: string;
    childNodes: IkDomNode[];
    attributes: Record<string, string>;
    style: Record<string, string>;
    textContent: string;
    getAttribute(name: string): string | null;
    querySelectorAll(selector: string): IkDomElement[];
    /**
     * Attribute names AS WRITTEN, keyed by the lower-case name used in `attributes`
     * ("viewbox" -> "viewBox"). `attributes` itself stays lower-case so every existing lookup keeps
     * working; this is what a serializer needs for SVG, whose attribute names are case-sensitive
     * (kHtmlDomWriterCore). Optional so other IkDomElement implementations stay valid.
     */
    attributeNames?: Record<string, string>;
    /** The tag name AS WRITTEN ("linearGradient"); `tagName` stays upper-case. Optional, as above. */
    sourceTagName?: string;
}

export interface IkDomDocument {
    body: IkDomElement;
}

/** An opening tag split into its parts — see kHtmlParserCore._ParseTagHeader. */
export interface IkTagHeader {
    TagName: string;
    AttrString: string;
    IsSelfClosing: boolean;
}

export class kHtmlParserCore {

    /**
     * Parses an HTML string into a lightweight DOM Document with a root body element.
     */
    public static ParseFromString = (Html: string): IkDomDocument => {
        if (getCoreUtils().KErrorMode) { return kHtmlParserCore._EmptyDocument(); }
        try {
            if (typeof Html !== "string" || Html.trim() === "") {
                return kHtmlParserCore._EmptyDocument();
            }

            // Strip scripts, styles, comments, and doctype
            let Clean: string = Html
                .replace(/<!DOCTYPE[^>]*>/gi, "")
                .replace(/<!--[\s\S]*?-->/g, "")
                .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, "")
                .replace(/<style\b[^<]*(?:(?!<\/style>)<[^<]*)*<\/style>/gi, "");

            // Extract content inside <body> if present
            const BodyMatch: RegExpExecArray | null = /<body\b[^>]*>([\s\S]*?)<\/body>/i.exec(Clean);
            if (BodyMatch !== null) {
                Clean = BodyMatch[1];
            } else {
                // Strip <html> and <head> tags
                Clean = Clean
                    .replace(/<\/?html\b[^>]*>/gi, "")
                    .replace(/<head\b[^<]*(?:(?!<\/head>)<[^<]*)*<\/head>/gi, "");
            }

            const Root: IkDomElement = kHtmlParserCore.CreateElement("BODY");
            kHtmlParserCore.BuildTree(Clean, Root);
            return { body: Root };
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "kHtmlParserCore.ParseFromString");
            const EmptyBody: IkDomElement = kHtmlParserCore.CreateElement("BODY");
            return { body: EmptyBody };
        }
    };

    /**
     * An empty document. DOCUMENTED EXCEPTION: a constant factory that IS the safe-default
     * ParseFromString returns from its own guard and catch.
     * ERROR-SUPPRESSED-JUSTIFIED: pure construction — every call site is guarded.
     */
    private static _EmptyDocument = (): IkDomDocument => {
        return { body: kHtmlParserCore._NewElement("BODY") };
    };

    /**
     * Creates a new DOM element.
     */
    public static CreateElement = (TagName: string): IkDomElement => {
        if (getCoreUtils().KErrorMode) { return kHtmlParserCore._NewElement(""); }
        try {
            return kHtmlParserCore._NewElement(TagName);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "kHtmlParserCore.CreateElement");
            return kHtmlParserCore._NewElement("");
        }
    };

    /**
     * The element literal itself. Its three members DELEGATE to named statics below rather than
     * carrying their bodies inline: that keeps this factory short, and it makes the tree-walking
     * logic testable on its own instead of only through a constructed element.
     *
     * DOCUMENTED EXCEPTION: a constant factory that IS the safe-default CreateElement returns from
     * its own guard and catch; gating it would recurse on its own output.
     * ERROR-SUPPRESSED-JUSTIFIED: pure object construction — every call site is guarded.
     */
    private static _NewElement = (TagName: string): IkDomElement => {
        return {
            nodeType: 1,
            nodeValue: null,
            tagName: TagName.toUpperCase(),
            sourceTagName: TagName,
            parentNode: null,
            childNodes: [],
            attributes: {},
            attributeNames: {},
            style: {},
            get textContent(): string { return kHtmlParserCore.TextContentOf(this); },
            getAttribute(Name: string): string | null {
                return kHtmlParserCore.AttributeOf(this, Name);
            },
            querySelectorAll(Selector: string): IkDomElement[] {
                return kHtmlParserCore.QuerySelectorAllOf(this, Selector);
            },
        };
    };

    /** The concatenated text of an element's whole subtree. */
    public static TextContentOf = (El: IkDomElement): string => {
        if (getCoreUtils().KErrorMode) { return ""; }
        try {
            let Text: string = "";
            for (const Child of El.childNodes) {
                if (Child.nodeType === 3) {
                    if (Child.nodeValue !== null) { Text = Text + Child.nodeValue; }
                    continue;
                }
                if (Child.nodeType === 1) { Text = Text + (Child as IkDomElement).textContent; }
            }
            return Text;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "kHtmlParserCore.TextContentOf");
            return "";
        }
    };

    /** One attribute, matched case-insensitively; null where absent, as the DOM answers. */
    public static AttributeOf = (El: IkDomElement, Name: string): string | null => {
        if (getCoreUtils().KErrorMode) { return null; }
        try {
            const Key: string = Name.toLowerCase();
            if (El.attributes[Key] !== undefined) { return El.attributes[Key]; }
            return null;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "kHtmlParserCore.AttributeOf");
            return null;
        }
    };

    /** Every descendant element matching a tag name, or all of them for "*". */
    public static QuerySelectorAllOf = (El: IkDomElement, Selector: string): IkDomElement[] => {
        if (getCoreUtils().KErrorMode) { return []; }
        try {
            const Results: IkDomElement[] = [];
            kHtmlParserCore._CollectMatching(El, Selector.toUpperCase(), Selector === "*", Results);
            return Results;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "kHtmlParserCore.QuerySelectorAllOf");
            return [];
        }
    };

    /** Depth-first collect. A named static rather than an inner closure (style rule: no anonymous
     *  functions), which also makes the recursion visible at the top level. */
    private static _CollectMatching = (Node: IkDomElement, TargetTag: string, MatchAll: boolean,
                                       Results: IkDomElement[]): void => {
        if (getCoreUtils().KErrorMode) { return; }
        try {
            for (const Kid of Node.childNodes) {
                if (Kid.nodeType !== 1) { continue; }
                const ElementKid: IkDomElement = Kid as IkDomElement;
                if (MatchAll === true || ElementKid.tagName === TargetTag) { Results.push(ElementKid); }
                kHtmlParserCore._CollectMatching(ElementKid, TargetTag, MatchAll, Results);
            }
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "kHtmlParserCore._CollectMatching");
        }
    };

    /**
     * Creates a new text node.
     */
    public static CreateTextNode = (Text: string): IkDomTextNode => {
        if (getCoreUtils().KErrorMode) { return { nodeType: 3, nodeValue: "", parentNode: null }; }
        try {
            return {
                nodeType: 3,
                nodeValue: kHtmlParserCore.DecodeEntities(Text),
                parentNode: null,
            };
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "kHtmlParserCore.CreateTextNode");
            return { nodeType: 3, nodeValue: "", parentNode: null };
        }
    };

    /**
     * Recursive tokenizer & tree builder.
     */
    /** HTML elements that never have a closing tag. A literal table, so a static field rather than
     *  a per-call object (it was rebuilt on every BuildTree recursion). */
    private static VoidTags: Record<string, boolean> = {
        AREA: true, BASE: true, BR: true, COL: true, EMBED: true, HR: true,
        IMG: true, INPUT: true, LINK: true, META: true, PARAM: true, SOURCE: true,
        TRACK: true, WBR: true,
    };

    /** True for an HTML element that never has a closing tag (BR, IMG, ...). Upper- or lower-case. */
    public static IsVoidTag = (TagName: string): boolean => {
        if (getCoreUtils().KErrorMode) { return false; }
        try {
            return kHtmlParserCore.VoidTags[String(TagName).toUpperCase()] === true;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "kHtmlParserCore.IsVoidTag");
            return false;
        }
    };

    private static BuildTree = (Html: string, Parent: IkDomElement): void => {
        if (getCoreUtils().KErrorMode) { return; }
        try {
            // Where each element closes, read from this text once per tag name (PX-128) - not once per element.
            const Closes: kHtmlCloseIndexCore = kHtmlCloseIndexCore.For(Html);
            let i: number = 0;
            while (i < Html.length) {
                const TagStart: number = kHtmlParserCore.NextTagStart(Html, i);
                if (TagStart === -1) {
                    kHtmlParserCore._AppendText(Html.substring(i), Parent, true);
                    break;
                }
                if (TagStart > i) { kHtmlParserCore._AppendText(Html.substring(i, TagStart), Parent, false); }

                const TagEnd: number = Html.indexOf(">", TagStart);
                if (TagEnd === -1) { break; }
                const TagContent: string = Html.substring(TagStart + 1, TagEnd).trim();
                i = TagEnd + 1;
                if (TagContent.startsWith("/")) { continue; }

                const Head: IkTagHeader = kHtmlParserCore._ParseTagHeader(TagContent);
                const Element: IkDomElement = kHtmlParserCore.CreateElement(Head.TagName);
                Element.parentNode = Parent;
                kHtmlParserCore.ParseAttributes(Head.AttrString, Element);
                Parent.childNodes.push(Element);
                if (Head.IsSelfClosing === true) { continue; }
                if (kHtmlParserCore.VoidTags[Element.tagName] === true) { continue; }

                const CloseTag: string = "</" + Head.TagName.toLowerCase() + ">";
                const InnerEnd: number = Closes.Find(Head.TagName, i);
                if (InnerEnd < 0) { continue; }
                kHtmlParserCore.BuildTree(Html.substring(i, InnerEnd), Element);
                i = InnerEnd + CloseTag.length;
            }
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "kHtmlParserCore.BuildTree");
        }
    };

    /**
     * Index of the next `<` that really opens markup, from From; -1 where there is none.
     *
     * A `<` opens markup only when a letter, `/`, `!` or `?` follows it - the HTML tokenizer's rule.
     * Any other `<` is TEXT ("a < b", "x <= 5", "1 <2"). Before 2026-09-26 every `<` was taken for a
     * tag, so "<p>a < b</p>" parsed as "a " and silently lost the rest: the text up to the next `>`
     * became a bogus element, and with no `>` at all everything after the `<` was dropped.
     */
    public static NextTagStart = (Html: string, From: number): number => {
        if (getCoreUtils().KErrorMode) { return -1; }
        try {
            let At: number = Html.indexOf("<", From);
            while (At !== -1) {
                if (/[A-Za-z\/!?]/.test(Html.charAt(At + 1)) === true) { return At; }
                At = Html.indexOf("<", At + 1);
            }
            return -1;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "kHtmlParserCore.NextTagStart");
            return -1;
        }
    };

    /** Append a text node, unless it is whitespace worth dropping. `Trailing` text is kept only
     *  when it carries something or a newline — the original rule, preserved exactly. */
    private static _AppendText = (Text: string, Parent: IkDomElement, Trailing: boolean): void => {
        if (getCoreUtils().KErrorMode) { return; }
        try {
            if (Trailing === true && Text.trim() === "" && Text.includes("\n") === false) { return; }
            if (Trailing === false && Text === "") { return; }
            const TextNode: IkDomTextNode = kHtmlParserCore.CreateTextNode(Text);
            TextNode.parentNode = Parent;
            Parent.childNodes.push(TextNode);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "kHtmlParserCore._AppendText");
        }
    };

    /** Split "div class='x'" / "br/" into its name, its attribute text and whether it self-closes. */
    private static _ParseTagHeader = (TagContent: string): IkTagHeader => {
        if (getCoreUtils().KErrorMode) { return { TagName: "", AttrString: "", IsSelfClosing: false }; }
        try {
            const IsSelfClosing: boolean = TagContent.endsWith("/");
            let Clean: string = TagContent;
            if (IsSelfClosing === true) { Clean = TagContent.substring(0, TagContent.length - 1).trim(); }
            const FirstSpace: number = Clean.search(/\s/);
            if (FirstSpace === -1) {
                return { TagName: Clean, AttrString: "", IsSelfClosing: IsSelfClosing };
            }
            return {
                TagName: Clean.substring(0, FirstSpace),
                AttrString: Clean.substring(FirstSpace + 1),
                IsSelfClosing: IsSelfClosing,
            };
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "kHtmlParserCore._ParseTagHeader");
            return { TagName: "", AttrString: "", IsSelfClosing: false };
        }
    };

    // The closing tag that matches an opening one (counting nested same-name tags, -1 where there is
    // none, which BuildTree treats as "leave the element empty") is found by kHtmlCloseIndexCore. It was
    // a loop here that lower-cased the whole text once per element: quadratic in the blocks (PX-128).

    /**
     * Parses attributes and inline styles on an element.
     *
     * Names are stored LOWER-CASE in `attributes` (the HTML rule, and what every consumer looks up),
     * with the spelling as written kept in `attributeNames` — SVG attribute names are case-sensitive
     * (`viewBox`, `preserveAspectRatio`), so a re-serialised inline SVG needs the original.
     * A name may carry a namespace prefix (`xlink:href`, `xml:space`); before 2026-09-26 the colon
     * split it into two bogus attributes (`xlink` = "" and `href` = the value).
     */
    private static ParseAttributes = (AttrStr: string, Element: IkDomElement): void => {
        if (getCoreUtils().KErrorMode) { return; }
        try {
            if (AttrStr.trim() === "") { return; }
            const AttrRegex: RegExp = /([a-zA-Z0-9_:-]+)(?:\s*=\s*(?:"([^"]*)"|'([^']*)'|([^\s>]+)))?/g;
            let Match: RegExpExecArray | null = AttrRegex.exec(AttrStr);
            while (Match !== null) {
                const AttrName: string = Match[1].toLowerCase();
                const AttrVal: string = kHtmlParserCore._FirstDefined(Match[2], Match[3], Match[4]);
                Element.attributes[AttrName] = kHtmlParserCore.DecodeEntities(AttrVal);
                if (Element.attributeNames !== undefined) { Element.attributeNames[AttrName] = Match[1]; }
                if (AttrName === "style") { kHtmlParserCore.ParseStyle(AttrVal, Element.style); }
                Match = AttrRegex.exec(AttrStr);
            }
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "kHtmlParserCore.ParseAttributes");
        }
    };

    /** The first of the three quoting alternatives the attribute regex matched, or "". Replaces a
     *  nest of three ternaries that had to be read right-to-left to work out. */
    private static _FirstDefined = (Double?: string, Single?: string, Bare?: string): string => {
        if (getCoreUtils().KErrorMode) { return ""; }
        try {
            if (Double !== undefined) { return Double; }
            if (Single !== undefined) { return Single; }
            if (Bare !== undefined) { return Bare; }
            return "";
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "kHtmlParserCore._FirstDefined");
            return "";
        }
    };

    /**
     * Parses CSS style string into style dictionary.
     */
    private static ParseStyle = (StyleStr: string, Target: Record<string, string>): void => {
        if (getCoreUtils().KErrorMode) { return; }
        try {
            for (const Decl of kCssDeclarationCore.Split(StyleStr)) {
                const ColonIdx: number = Decl.indexOf(":");
                if (ColonIdx <= 0) { continue; }
                const Prop: string = Decl.substring(0, ColonIdx).trim().toLowerCase();
                const Val: string = Decl.substring(ColonIdx + 1).trim();
                Target[Prop] = Val;
                // CamelCase mapping for DOM style property access
                Target[kHtmlParserCore._CamelCase(Prop)] = Val;
            }
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "kHtmlParserCore.ParseStyle");
        }
    };

    /** "font-size" -> "fontSize". A hand-rolled walk rather than a replace with a callback, because
     *  the style rule forbids an anonymous replacer and a named one for two lines is worse. */
    private static _CamelCase = (Prop: string): string => {
        if (getCoreUtils().KErrorMode) { return ""; }
        try {
            const Parts: string[] = Prop.split("-");
            let Out: string = Parts[0];
            for (let Index: number = 1; Index < Parts.length; Index++) {
                const Part: string = Parts[Index];
                if (Part === "") { continue; }
                Out = Out + Part.charAt(0).toUpperCase() + Part.substring(1);
            }
            return Out;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "kHtmlParserCore._CamelCase");
            return "";
        }
    };

    /**
     * Decodes basic HTML entities, the way a browser's parser does.
     *
     * `&nbsp;` is U+00A0, NOT a plain space (changed 2026-09-26). Decoding it to " " made an empty
     * spacing paragraph (`<p>&nbsp;</p>`, what every rich-text editor writes) collapse to nothing in
     * any consumer that collapses whitespace, and made the server parse disagree with the browser's
     * DOMParser. Consumers that collapse whitespace do it over ASCII whitespace only
     * (kDocHtmlInlineCore.PushText), exactly as HTML rendering does.
     *
     * ONE pass over the text (2026-09-26): each `&...;` reference is decoded exactly once, so an
     * escaped entity stays literal text - "&amp;lt;" is "&lt;", not "<". The old chain of replaces
     * decoded `&amp;` first and then decoded its own output a second time. Unknown names and
     * out-of-range code points are left as written, as a browser does.
     */
    // AllowedReferences restricts complete reference names (including "#39") for legacy text
    // contracts. null keeps the full HTML decoder; an empty list decodes nothing.
    public static DecodeEntities = (Text: string, AllowedReferences: readonly string[] | null = null): string => {
        if (getCoreUtils().KErrorMode) { return Text; }
        try {
            if (Text.includes("&") === false) { return Text; }
            // A name may carry digits after its first letter (&sup2; &frac12; &there4;).
            const Pattern: RegExp = /&(#[xX][0-9a-fA-F]+|#[0-9]+|[a-zA-Z][a-zA-Z0-9]*);/g;
            let Out: string = "";
            let Last: number = 0;
            let Match: RegExpExecArray | null = Pattern.exec(Text);
            while (Match !== null) {
                Out = Out + Text.substring(Last, Match.index) + kHtmlParserCore._EntityValue(Match[0], Match[1], AllowedReferences);
                Last = Match.index + Match[0].length;
                Match = Pattern.exec(Text);
            }
            return Out + Text.substring(Last);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "kHtmlParserCore.DecodeEntities");
            return Text;
        }
    };

    /**
     * The named references decoded: all 252 of HTML 4.01 (Latin-1, "special" - quotes, dashes, spaces,
     * euro - and "symbols" - Greek, math, arrows, general punctuation) plus XML's `apos`. This is the
     * set a rich-text editor, Word or a web page emits. Until 2026-09-27 (PX-51) only amp/lt/gt/quot/
     * apos/nbsp were known, so `&shy;` `&ndash;` `&hellip;` `&euro;` ... stayed literal and printed as
     * six-letter text in PDF/Word while the browser showed the character.
     *
     * Values are char codes (not literal characters) so the source never carries an invisible or
     * look-alike character (`shy`, `zwnj`, `thinsp`, `minus`). Names are CASE-SENSITIVE, as in HTML:
     * `Aring` is U+00C5 and `aring` U+00E5. `lang` / `rang` use the HTML5 code points (U+27E8/U+27E9)
     * that browsers decode today, not HTML 4's U+2329/U+232A. A literal table, so a static field;
     * the lookup in _EntityValue is one own-property probe.
     */
    public static NamedEntities: Record<string, string> = {
        // Markup-significant and XML
        quot: String.fromCharCode(34), amp: String.fromCharCode(38), apos: String.fromCharCode(39),
        lt: String.fromCharCode(60), gt: String.fromCharCode(62),
        // Latin-1 (U+00A0 - U+00FF)
        nbsp: String.fromCharCode(160), iexcl: String.fromCharCode(161), cent: String.fromCharCode(162),
        pound: String.fromCharCode(163), curren: String.fromCharCode(164), yen: String.fromCharCode(165),
        brvbar: String.fromCharCode(166), sect: String.fromCharCode(167), uml: String.fromCharCode(168),
        copy: String.fromCharCode(169), ordf: String.fromCharCode(170), laquo: String.fromCharCode(171),
        not: String.fromCharCode(172), shy: String.fromCharCode(173), reg: String.fromCharCode(174),
        macr: String.fromCharCode(175), deg: String.fromCharCode(176), plusmn: String.fromCharCode(177),
        sup2: String.fromCharCode(178), sup3: String.fromCharCode(179), acute: String.fromCharCode(180),
        micro: String.fromCharCode(181), para: String.fromCharCode(182), middot: String.fromCharCode(183),
        cedil: String.fromCharCode(184), sup1: String.fromCharCode(185), ordm: String.fromCharCode(186),
        raquo: String.fromCharCode(187), frac14: String.fromCharCode(188), frac12: String.fromCharCode(189),
        frac34: String.fromCharCode(190), iquest: String.fromCharCode(191),
        Agrave: String.fromCharCode(192), Aacute: String.fromCharCode(193), Acirc: String.fromCharCode(194),
        Atilde: String.fromCharCode(195), Auml: String.fromCharCode(196), Aring: String.fromCharCode(197),
        AElig: String.fromCharCode(198), Ccedil: String.fromCharCode(199), Egrave: String.fromCharCode(200),
        Eacute: String.fromCharCode(201), Ecirc: String.fromCharCode(202), Euml: String.fromCharCode(203),
        Igrave: String.fromCharCode(204), Iacute: String.fromCharCode(205), Icirc: String.fromCharCode(206),
        Iuml: String.fromCharCode(207), ETH: String.fromCharCode(208), Ntilde: String.fromCharCode(209),
        Ograve: String.fromCharCode(210), Oacute: String.fromCharCode(211), Ocirc: String.fromCharCode(212),
        Otilde: String.fromCharCode(213), Ouml: String.fromCharCode(214), times: String.fromCharCode(215),
        Oslash: String.fromCharCode(216), Ugrave: String.fromCharCode(217), Uacute: String.fromCharCode(218),
        Ucirc: String.fromCharCode(219), Uuml: String.fromCharCode(220), Yacute: String.fromCharCode(221),
        THORN: String.fromCharCode(222), szlig: String.fromCharCode(223),
        agrave: String.fromCharCode(224), aacute: String.fromCharCode(225), acirc: String.fromCharCode(226),
        atilde: String.fromCharCode(227), auml: String.fromCharCode(228), aring: String.fromCharCode(229),
        aelig: String.fromCharCode(230), ccedil: String.fromCharCode(231), egrave: String.fromCharCode(232),
        eacute: String.fromCharCode(233), ecirc: String.fromCharCode(234), euml: String.fromCharCode(235),
        igrave: String.fromCharCode(236), iacute: String.fromCharCode(237), icirc: String.fromCharCode(238),
        iuml: String.fromCharCode(239), eth: String.fromCharCode(240), ntilde: String.fromCharCode(241),
        ograve: String.fromCharCode(242), oacute: String.fromCharCode(243), ocirc: String.fromCharCode(244),
        otilde: String.fromCharCode(245), ouml: String.fromCharCode(246), divide: String.fromCharCode(247),
        oslash: String.fromCharCode(248), ugrave: String.fromCharCode(249), uacute: String.fromCharCode(250),
        ucirc: String.fromCharCode(251), uuml: String.fromCharCode(252), yacute: String.fromCharCode(253),
        thorn: String.fromCharCode(254), yuml: String.fromCharCode(255),
        // Latin Extended and spacing modifiers
        OElig: String.fromCharCode(338), oelig: String.fromCharCode(339), Scaron: String.fromCharCode(352),
        scaron: String.fromCharCode(353), Yuml: String.fromCharCode(376), fnof: String.fromCharCode(402),
        circ: String.fromCharCode(710), tilde: String.fromCharCode(732),
        // Greek
        Alpha: String.fromCharCode(913), Beta: String.fromCharCode(914), Gamma: String.fromCharCode(915),
        Delta: String.fromCharCode(916), Epsilon: String.fromCharCode(917), Zeta: String.fromCharCode(918),
        Eta: String.fromCharCode(919), Theta: String.fromCharCode(920), Iota: String.fromCharCode(921),
        Kappa: String.fromCharCode(922), Lambda: String.fromCharCode(923), Mu: String.fromCharCode(924),
        Nu: String.fromCharCode(925), Xi: String.fromCharCode(926), Omicron: String.fromCharCode(927),
        Pi: String.fromCharCode(928), Rho: String.fromCharCode(929), Sigma: String.fromCharCode(931),
        Tau: String.fromCharCode(932), Upsilon: String.fromCharCode(933), Phi: String.fromCharCode(934),
        Chi: String.fromCharCode(935), Psi: String.fromCharCode(936), Omega: String.fromCharCode(937),
        alpha: String.fromCharCode(945), beta: String.fromCharCode(946), gamma: String.fromCharCode(947),
        delta: String.fromCharCode(948), epsilon: String.fromCharCode(949), zeta: String.fromCharCode(950),
        eta: String.fromCharCode(951), theta: String.fromCharCode(952), iota: String.fromCharCode(953),
        kappa: String.fromCharCode(954), lambda: String.fromCharCode(955), mu: String.fromCharCode(956),
        nu: String.fromCharCode(957), xi: String.fromCharCode(958), omicron: String.fromCharCode(959),
        pi: String.fromCharCode(960), rho: String.fromCharCode(961), sigmaf: String.fromCharCode(962),
        sigma: String.fromCharCode(963), tau: String.fromCharCode(964), upsilon: String.fromCharCode(965),
        phi: String.fromCharCode(966), chi: String.fromCharCode(967), psi: String.fromCharCode(968),
        omega: String.fromCharCode(969), thetasym: String.fromCharCode(977), upsih: String.fromCharCode(978),
        piv: String.fromCharCode(982),
        // General punctuation: spaces, joiners, direction marks, dashes, quotes
        ensp: String.fromCharCode(8194), emsp: String.fromCharCode(8195), thinsp: String.fromCharCode(8201),
        zwnj: String.fromCharCode(8204), zwj: String.fromCharCode(8205), lrm: String.fromCharCode(8206),
        rlm: String.fromCharCode(8207), ndash: String.fromCharCode(8211), mdash: String.fromCharCode(8212),
        lsquo: String.fromCharCode(8216), rsquo: String.fromCharCode(8217), sbquo: String.fromCharCode(8218),
        ldquo: String.fromCharCode(8220), rdquo: String.fromCharCode(8221), bdquo: String.fromCharCode(8222),
        dagger: String.fromCharCode(8224), Dagger: String.fromCharCode(8225), bull: String.fromCharCode(8226),
        hellip: String.fromCharCode(8230), permil: String.fromCharCode(8240), prime: String.fromCharCode(8242),
        Prime: String.fromCharCode(8243), lsaquo: String.fromCharCode(8249), rsaquo: String.fromCharCode(8250),
        oline: String.fromCharCode(8254), frasl: String.fromCharCode(8260), euro: String.fromCharCode(8364),
        // Letterlike symbols and arrows
        image: String.fromCharCode(8465), weierp: String.fromCharCode(8472), real: String.fromCharCode(8476),
        trade: String.fromCharCode(8482), alefsym: String.fromCharCode(8501), larr: String.fromCharCode(8592),
        uarr: String.fromCharCode(8593), rarr: String.fromCharCode(8594), darr: String.fromCharCode(8595),
        harr: String.fromCharCode(8596), crarr: String.fromCharCode(8629), lArr: String.fromCharCode(8656),
        uArr: String.fromCharCode(8657), rArr: String.fromCharCode(8658), dArr: String.fromCharCode(8659),
        hArr: String.fromCharCode(8660),
        // Mathematical operators
        forall: String.fromCharCode(8704), part: String.fromCharCode(8706), exist: String.fromCharCode(8707),
        empty: String.fromCharCode(8709), nabla: String.fromCharCode(8711), isin: String.fromCharCode(8712),
        notin: String.fromCharCode(8713), ni: String.fromCharCode(8715), prod: String.fromCharCode(8719),
        sum: String.fromCharCode(8721), minus: String.fromCharCode(8722), lowast: String.fromCharCode(8727),
        radic: String.fromCharCode(8730), prop: String.fromCharCode(8733), infin: String.fromCharCode(8734),
        ang: String.fromCharCode(8736), and: String.fromCharCode(8743), or: String.fromCharCode(8744),
        cap: String.fromCharCode(8745), cup: String.fromCharCode(8746), int: String.fromCharCode(8747),
        there4: String.fromCharCode(8756), sim: String.fromCharCode(8764), cong: String.fromCharCode(8773),
        asymp: String.fromCharCode(8776), ne: String.fromCharCode(8800), equiv: String.fromCharCode(8801),
        le: String.fromCharCode(8804), ge: String.fromCharCode(8805), sub: String.fromCharCode(8834),
        sup: String.fromCharCode(8835), nsub: String.fromCharCode(8836), sube: String.fromCharCode(8838),
        supe: String.fromCharCode(8839), oplus: String.fromCharCode(8853), otimes: String.fromCharCode(8855),
        perp: String.fromCharCode(8869), sdot: String.fromCharCode(8901),
        // Technical, geometric and card symbols
        lceil: String.fromCharCode(8968), rceil: String.fromCharCode(8969), lfloor: String.fromCharCode(8970),
        rfloor: String.fromCharCode(8971), lang: String.fromCharCode(10216), rang: String.fromCharCode(10217),
        loz: String.fromCharCode(9674), spades: String.fromCharCode(9824), clubs: String.fromCharCode(9827),
        hearts: String.fromCharCode(9829), diams: String.fromCharCode(9830),
    };

    /** The text one `&...;` reference stands for (`Ref` is what sits between & and ;), or the
     *  reference itself when it is unknown or not a valid code point. */
    private static _EntityValue = (Whole: string, Ref: string, AllowedReferences: readonly string[] | null = null): string => {
        if (getCoreUtils().KErrorMode) { return Whole; }
        try {
            if (AllowedReferences !== null && AllowedReferences.indexOf(Ref) === -1) { return Whole; }
            if (Ref.charAt(0) !== "#") {
                // Own properties only: "&constructor;" must not answer Object.prototype.constructor.
                if (Object.prototype.hasOwnProperty.call(kHtmlParserCore.NamedEntities, Ref) === false) { return Whole; }
                return kHtmlParserCore.NamedEntities[Ref];
            }
            let Code: number = parseInt(Ref.substring(1), 10);
            if (Ref.charAt(1) === "x" || Ref.charAt(1) === "X") { Code = parseInt(Ref.substring(2), 16); }
            if (isFinite(Code) === false || Code < 0 || Code > 0x10FFFF) { return Whole; }
            return String.fromCodePoint(Code);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "kHtmlParserCore._EntityValue");
            return Whole;
        }
    };
}
